import { HelpCircle, LogOut, User as UserIcon, Settings, ChevronDown, Menu } from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth, useHasPermission, useFeature } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { MobileMenu } from './MobileMenu';

const baseNavItems = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/opportunities', label: 'Pipeline' },
  { to: '/projects', label: 'Projects' },
  { to: '/customers', label: 'Customers' },
];

const deliveryNavItem = { to: '/delivery', label: 'Delivery' };
const reviewsNavItem = { to: '/reviews', label: 'Reviews' };
const forecastNavItem = { to: '/forecast', label: 'Forecast' };

const adminNavItem = { to: '/admin', label: 'Admin' };

export function Header() {
  const { user, logout, isLoggingOut } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasAdminAccess = useHasPermission('admin.users.view');
  const showDelivery = useFeature('delivery_page');
  const showReviews = useFeature('review_workflow');
  const showForecast = useFeature('forecast_scenarios');

  const navItems = useMemo(() => {
    const items = [...baseNavItems];
    if (showDelivery) {
      items.splice(3, 0, deliveryNavItem); // after Projects
    }
    if (showReviews) {
      const pipelineIdx = items.findIndex((i) => i.to === '/opportunities');
      items.splice(pipelineIdx + 1, 0, reviewsNavItem);
    }
    if (showForecast) {
      items.push(forecastNavItem);
    }
    return items;
  }, [showDelivery, showReviews, showForecast]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <header className="flex h-14 items-center justify-between bg-white px-4 md:px-6 shadow-header relative z-10">
        {/* Left side - Hamburger, Logo and Navigation */}
        <div className="flex items-center gap-4 md:gap-8">
          {/* Hamburger menu - mobile only */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 transition-colors md:hidden touch-target"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src="/xtravirt-logo.png"
              alt="Xtravirt"
              className="h-6"
            />
            <span className="font-heading text-xl font-bold text-xtravirt-dark hidden sm:inline">
              PST
            </span>
          </Link>

        {/* Navigation */}
        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'nav-link relative px-4 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'nav-link-active text-xtravirt-dark'
                    : 'text-gray-600 hover:text-xtravirt-dark'
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          {hasAdminAccess && (
            <NavLink
              to={adminNavItem.to}
              className={({ isActive }) =>
                cn(
                  'nav-link relative px-4 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'nav-link-active text-xtravirt-dark'
                    : 'text-gray-600 hover:text-xtravirt-dark'
                )
              }
            >
              {adminNavItem.label}
            </NavLink>
          )}
        </nav>
      </div>

        {/* Right side - Version, Help, User */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Version badge - hidden on small mobile */}
          <span className="version-badge hidden sm:inline-flex">
            v1.0.0
          </span>

          {/* Help button - hidden on mobile (available in mobile menu) */}
          <button
            className="help-btn hidden md:flex"
            aria-label="Help"
            title="Help"
          >
            <HelpCircle className="h-4 w-4" />
          </button>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="user-menu-btn flex items-center gap-2"
          >
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="h-8 w-8 rounded-full"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-xtravirt-navy text-white text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <ChevronDown className={cn(
              'h-4 w-4 text-gray-500 transition-transform',
              dropdownOpen && 'rotate-180'
            )} />
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div
              className={cn(
                'absolute right-0 top-full mt-2 w-56 origin-top-right rounded-md bg-white shadow-dropdown border border-gray-200',
                'animate-fade-in z-50'
              )}
            >
              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
                {user?.isSuperAdmin && (
                  <span className="mt-1 inline-block rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                    Super Admin
                  </span>
                )}
              </div>

              <div className="py-1">
                <Link
                  to="/settings/profile"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setDropdownOpen(false)}
                >
                  <UserIcon className="h-4 w-4" />
                  Profile
                </Link>
                <Link
                  to="/settings/tokens"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setDropdownOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  API Tokens
                </Link>
              </div>

              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    logout();
                  }}
                  disabled={isLoggingOut}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <LogOut className="h-4 w-4" />
                  {isLoggingOut ? 'Signing out...' : 'Sign out'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </>
  );
}
